/**
 * Document Taxonomy
 *
 * Shared category/sub-type hierarchy for user documents — used by backend (validation,
 * persistence) and frontend/mobile (upload UX, filtering). Designed to be international:
 * identity sub-types cover passport/national-ID/tax-ID concepts rather than any one country's
 * documents specifically.
 */
// ─── Top-level categories ──────────────────────────────────────────────────────
export var DocumentCategory;
(function (DocumentCategory) {
    DocumentCategory["PERSONAL_IDENTITY"] = "PERSONAL_IDENTITY";
    DocumentCategory["ACADEMIC"] = "ACADEMIC";
    DocumentCategory["EMPLOYMENT"] = "EMPLOYMENT";
    DocumentCategory["FINANCIAL"] = "FINANCIAL";
    DocumentCategory["CERTIFICATION"] = "CERTIFICATION";
    DocumentCategory["CAREER"] = "CAREER";
    DocumentCategory["LEGAL"] = "LEGAL";
    DocumentCategory["OTHER"] = "OTHER";
})(DocumentCategory || (DocumentCategory = {}));
// ─── Sub-types ──────────────────────────────────────────────────────────────────
export var DocumentType;
(function (DocumentType) {
    // Personal / identity
    DocumentType["PASSPORT"] = "PASSPORT";
    DocumentType["NATIONAL_ID"] = "NATIONAL_ID";
    DocumentType["DRIVING_LICENSE"] = "DRIVING_LICENSE";
    DocumentType["TAX_ID"] = "TAX_ID";
    DocumentType["VISA_WORK_PERMIT"] = "VISA_WORK_PERMIT";
    DocumentType["BIRTH_CERTIFICATE"] = "BIRTH_CERTIFICATE";
    DocumentType["ADDRESS_PROOF"] = "ADDRESS_PROOF";
    DocumentType["PHOTOGRAPH"] = "PHOTOGRAPH";
    DocumentType["OTHER_IDENTITY"] = "OTHER_IDENTITY";
    // Academic
    DocumentType["MARKSHEET_TRANSCRIPT"] = "MARKSHEET_TRANSCRIPT";
    DocumentType["DEGREE_CERTIFICATE"] = "DEGREE_CERTIFICATE";
    DocumentType["DIPLOMA_CERTIFICATE"] = "DIPLOMA_CERTIFICATE";
    DocumentType["PROVISIONAL_CERTIFICATE"] = "PROVISIONAL_CERTIFICATE";
    DocumentType["MIGRATION_CERTIFICATE"] = "MIGRATION_CERTIFICATE";
    DocumentType["SCHOOL_LEAVING_CERTIFICATE"] = "SCHOOL_LEAVING_CERTIFICATE";
    DocumentType["SYLLABUS"] = "SYLLABUS";
    DocumentType["OTHER_ACADEMIC"] = "OTHER_ACADEMIC";
    // Employment / experience
    DocumentType["OFFER_LETTER"] = "OFFER_LETTER";
    DocumentType["APPOINTMENT_LETTER"] = "APPOINTMENT_LETTER";
    DocumentType["EXPERIENCE_LETTER"] = "EXPERIENCE_LETTER";
    DocumentType["RELIEVING_LETTER"] = "RELIEVING_LETTER";
    DocumentType["RESIGNATION_LETTER"] = "RESIGNATION_LETTER";
    DocumentType["SALARY_SLIP"] = "SALARY_SLIP";
    DocumentType["INCREMENT_LETTER"] = "INCREMENT_LETTER";
    DocumentType["PROMOTION_LETTER"] = "PROMOTION_LETTER";
    DocumentType["EMPLOYMENT_CONTRACT"] = "EMPLOYMENT_CONTRACT";
    DocumentType["OTHER_EMPLOYMENT"] = "OTHER_EMPLOYMENT";
    // Financial
    DocumentType["BANK_STATEMENT"] = "BANK_STATEMENT";
    DocumentType["SALARY_CERTIFICATE"] = "SALARY_CERTIFICATE";
    DocumentType["TAX_RETURN"] = "TAX_RETURN";
    DocumentType["OTHER_FINANCIAL"] = "OTHER_FINANCIAL";
    // Certifications & skills
    DocumentType["PROFESSIONAL_CERTIFICATION"] = "PROFESSIONAL_CERTIFICATION";
    DocumentType["COURSE_COMPLETION_CERTIFICATE"] = "COURSE_COMPLETION_CERTIFICATE";
    DocumentType["LANGUAGE_PROFICIENCY_CERTIFICATE"] = "LANGUAGE_PROFICIENCY_CERTIFICATE";
    DocumentType["PROFESSIONAL_LICENSE"] = "PROFESSIONAL_LICENSE";
    DocumentType["OTHER_CERTIFICATION"] = "OTHER_CERTIFICATION";
    // Career documents
    DocumentType["RESUME"] = "RESUME";
    DocumentType["COVER_LETTER"] = "COVER_LETTER";
    DocumentType["PORTFOLIO"] = "PORTFOLIO";
    DocumentType["RECOMMENDATION_LETTER"] = "RECOMMENDATION_LETTER";
    DocumentType["REFERENCE_LETTER"] = "REFERENCE_LETTER";
    // Legal
    DocumentType["NDA"] = "NDA";
    DocumentType["BACKGROUND_CHECK"] = "BACKGROUND_CHECK";
    DocumentType["POLICE_CLEARANCE_CERTIFICATE"] = "POLICE_CLEARANCE_CERTIFICATE";
    DocumentType["OTHER_LEGAL"] = "OTHER_LEGAL";
    // Fallback
    DocumentType["OTHER"] = "OTHER";
})(DocumentType || (DocumentType = {}));
export const DOCUMENT_TAXONOMY = [
    {
        value: DocumentCategory.PERSONAL_IDENTITY,
        label: 'Personal & Identity',
        description: 'Government-issued ID, proof of address, and personal records',
        types: [
            { value: DocumentType.PASSPORT, label: 'Passport' },
            { value: DocumentType.NATIONAL_ID, label: 'National ID / Aadhaar / Resident Card' },
            { value: DocumentType.DRIVING_LICENSE, label: 'Driving License' },
            { value: DocumentType.TAX_ID, label: 'Tax ID (PAN / SSN / TIN / NIN)' },
            { value: DocumentType.VISA_WORK_PERMIT, label: 'Visa / Work Permit' },
            { value: DocumentType.BIRTH_CERTIFICATE, label: 'Birth Certificate' },
            { value: DocumentType.ADDRESS_PROOF, label: 'Address Proof / Utility Bill' },
            { value: DocumentType.PHOTOGRAPH, label: 'Photograph' },
            { value: DocumentType.OTHER_IDENTITY, label: 'Other Identity Document' },
        ],
    },
    {
        value: DocumentCategory.ACADEMIC,
        label: 'Academic',
        description: 'Education records, transcripts, and school/university certificates',
        types: [
            { value: DocumentType.MARKSHEET_TRANSCRIPT, label: 'Marksheet / Transcript' },
            { value: DocumentType.DEGREE_CERTIFICATE, label: 'Degree Certificate' },
            { value: DocumentType.DIPLOMA_CERTIFICATE, label: 'Diploma Certificate' },
            { value: DocumentType.PROVISIONAL_CERTIFICATE, label: 'Provisional Certificate' },
            { value: DocumentType.MIGRATION_CERTIFICATE, label: 'Migration Certificate' },
            { value: DocumentType.SCHOOL_LEAVING_CERTIFICATE, label: 'School Leaving Certificate' },
            { value: DocumentType.SYLLABUS, label: 'Syllabus' },
            { value: DocumentType.OTHER_ACADEMIC, label: 'Other Academic Document' },
        ],
    },
    {
        value: DocumentCategory.EMPLOYMENT,
        label: 'Employment & Experience',
        description: 'Letters and records from current or past employers',
        types: [
            { value: DocumentType.OFFER_LETTER, label: 'Offer Letter' },
            { value: DocumentType.APPOINTMENT_LETTER, label: 'Appointment Letter' },
            { value: DocumentType.EXPERIENCE_LETTER, label: 'Experience Letter' },
            { value: DocumentType.RELIEVING_LETTER, label: 'Relieving Letter' },
            { value: DocumentType.RESIGNATION_LETTER, label: 'Resignation Letter' },
            { value: DocumentType.SALARY_SLIP, label: 'Salary Slip / Payslip' },
            { value: DocumentType.INCREMENT_LETTER, label: 'Increment Letter' },
            { value: DocumentType.PROMOTION_LETTER, label: 'Promotion Letter' },
            { value: DocumentType.EMPLOYMENT_CONTRACT, label: 'Employment Contract' },
            { value: DocumentType.OTHER_EMPLOYMENT, label: 'Other Employment Document' },
        ],
    },
    {
        value: DocumentCategory.FINANCIAL,
        label: 'Financial',
        description: 'Bank, salary, and tax records',
        types: [
            { value: DocumentType.BANK_STATEMENT, label: 'Bank Statement' },
            { value: DocumentType.SALARY_CERTIFICATE, label: 'Salary Certificate' },
            { value: DocumentType.TAX_RETURN, label: 'Tax Return' },
            { value: DocumentType.OTHER_FINANCIAL, label: 'Other Financial Document' },
        ],
    },
    {
        value: DocumentCategory.CERTIFICATION,
        label: 'Certifications & Skills',
        description: 'Professional certifications, courses, and licenses',
        types: [
            { value: DocumentType.PROFESSIONAL_CERTIFICATION, label: 'Professional Certification' },
            {
                value: DocumentType.COURSE_COMPLETION_CERTIFICATE,
                label: 'Course Completion Certificate',
            },
            {
                value: DocumentType.LANGUAGE_PROFICIENCY_CERTIFICATE,
                label: 'Language Proficiency Certificate',
            },
            { value: DocumentType.PROFESSIONAL_LICENSE, label: 'Professional License' },
            { value: DocumentType.OTHER_CERTIFICATION, label: 'Other Certification' },
        ],
    },
    {
        value: DocumentCategory.CAREER,
        label: 'Career Documents',
        description: 'Resumes, cover letters, portfolios, and references',
        types: [
            { value: DocumentType.RESUME, label: 'Resume / CV' },
            { value: DocumentType.COVER_LETTER, label: 'Cover Letter' },
            { value: DocumentType.PORTFOLIO, label: 'Portfolio' },
            { value: DocumentType.RECOMMENDATION_LETTER, label: 'Recommendation Letter' },
            { value: DocumentType.REFERENCE_LETTER, label: 'Reference Letter' },
        ],
    },
    {
        value: DocumentCategory.LEGAL,
        label: 'Legal',
        description: 'Agreements and background/clearance records',
        types: [
            { value: DocumentType.NDA, label: 'NDA' },
            { value: DocumentType.BACKGROUND_CHECK, label: 'Background Check' },
            {
                value: DocumentType.POLICE_CLEARANCE_CERTIFICATE,
                label: 'Police Clearance Certificate',
            },
            { value: DocumentType.OTHER_LEGAL, label: 'Other Legal Document' },
        ],
    },
    {
        value: DocumentCategory.OTHER,
        label: 'Other',
        description: 'Anything that does not fit the categories above',
        types: [{ value: DocumentType.OTHER, label: 'Other' }],
    },
];
export const DOCUMENT_TYPE_TO_CATEGORY = DOCUMENT_TAXONOMY.reduce((acc, cat) => {
    for (const t of cat.types)
        acc[t.value] = cat.value;
    return acc;
}, {});
